import Alert from '@mdi/svg/svg/alert.svg?react';
import { Button, Flex, MDIIcon } from '@solinkcorp/solink-components';
import { PlatformApi } from 'clients';
import { type IEvent } from 'clients/PlatformApi';
import Event from 'components/EventsPage/Event/Event';
import { type FC, useRef, useState } from 'react';
import useLocale from 'src/hooks/useLocale';
import useSWR from 'swr';
import LoadingCylonSvg from '../../images/loading-cylon.svg';

const MAX_EVENTS_QUERY_RETRY = 3;

const SpotCheckEventPreview: FC<{
  eventPreviewId?: string;
  onCreateSpotCheckPreview: () => Promise<void>;
  isPreviewDisabled: boolean;
}> = ({ eventPreviewId, onCreateSpotCheckPreview, isPreviewDisabled }) => {
  const locale = useLocale();
  const [eventPollingState, setEventPollingState] = useState<
    'POLLING' | 'SUCCESSFUL' | 'PREVIEW_ERROR'
  >('POLLING');
  const queryCountRef = useRef(0);

  const { data: event, error } = useSWR(
    eventPreviewId ? ['spot-check-event-preview', eventPreviewId] : null,
    ([, eventId]) => {
      queryCountRef.current += 1;
      return PlatformApi().getEvent(eventId);
    },
    {
      refreshInterval: eventPollingState === 'POLLING' ? 10000 : 0,
      revalidateOnFocus: false,
      revalidateIfStale: false,
      errorRetryCount: MAX_EVENTS_QUERY_RETRY,
      onSuccess: (event: IEvent) => {
        if (!event?.videoLinkItems) {
          return;
        }

        const linkItemsHaveError =
          event?.videoLinkItems && event?.videoLinkItems?.some((item) => item?.metadata?.isError);

        if (linkItemsHaveError) {
          setEventPollingState('PREVIEW_ERROR');
        } else if ((event?.videoLinkItems?.length ?? 0) > 1) {
          /** spot check events have prompt and response as videoLinkItems */
          setEventPollingState('SUCCESSFUL');
        }

        return event;
      },
    },
  );

  if (!eventPreviewId) {
    return (
      <Flex
        className="spot-check-event-preview-container"
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
      >
        <p>{locale.scheduledAIChat.noPreviewAvailable}</p>
        <Button disabled={isPreviewDisabled} isPrimary={true} onClick={onCreateSpotCheckPreview}>
          {locale.general.preview}
        </Button>
      </Flex>
    );
  }

  if (queryCountRef.current >= MAX_EVENTS_QUERY_RETRY && error) {
    return (
      <Flex flexDirection="column" alignItems="center">
        <MDIIcon icon={Alert} fill="var(--color-red)" />
        <p style={{ textAlign: 'center' }}>{locale.scheduledAIChat.errorQueryingPreviewEvent}</p>
        <Button disabled={isPreviewDisabled} isPrimary={true} onClick={onCreateSpotCheckPreview}>
          {locale.scheduledAIChat.retryPreview}
        </Button>
      </Flex>
    );
  }

  return (
    <Flex flexDirection="column">
      {eventPollingState === 'PREVIEW_ERROR' && (
        <p className="spot-check-preview-ai-error">
          {locale.scheduledAIChat.spotCheckPreviewError} <br /> {locale.general.pleaseTryAgain}
        </p>
      )}
      {eventPollingState === 'POLLING' && (
        <Flex
          className="spot-check-event-preview-container"
          justifyContent="center"
          alignItems="center"
        >
          <p className="refresh-event-preview-message">
            {locale.general.loading} {locale.general.preview}
          </p>
          <img src={LoadingCylonSvg} title={locale.general.loading} />
        </Flex>
      )}
      {event && (
        <Event
          key={`event-item-${eventPreviewId}`}
          event={event}
          eventFilters={{
            transaction: false,
            motion: false,
            bookmark: true,
            saved: false,
          }}
          hideActionMenu={true}
          layoutType="PEEK"
        />
      )}
    </Flex>
  );
};

export default SpotCheckEventPreview;
